import React, { type ReactNode, type ComponentType } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { FieldCategoryWithFields } from '../../types/layout-items.tsx';
import { Column } from '../styled.tsx';
import { Divider } from './divider/index.tsx';
import type { Props } from './types.tsx';
import { WhatIsThisWithInProductHelp } from './what-is-this-with-in-product-help/index.tsx';
import { WhatIsThis } from './what-is-this/index.tsx';

type RenderWhatIsThisProps = {
	readonly category: FieldCategoryWithFields;
	readonly fieldsDocumentationUrl: string | undefined;
	readonly formatMessage: FormatMessage;
	readonly whatsThisLabelIdRef: string;
};

export const renderWhatIsThis = ({
	category,
	fieldsDocumentationUrl,
	whatsThisLabelIdRef,
}: RenderWhatIsThisProps) => {
	const { whatIsThis } = category.configuration;
	if (!whatIsThis) {
		return null;
	}

	return (
		<WhatIsThisWithInProductHelp
			whatsThisLabelIdRef={whatsThisLabelIdRef}
			buttonLabel={whatIsThis.buttonLabel}
			description={whatIsThis.content}
			linkToDocumentationText={whatIsThis.linkToDocumentationText}
			itemDocumentationUrl={fieldsDocumentationUrl}
			linkToInProductHelpArticleId={whatIsThis.linkToInProductHelpArticleId}
		/>
	);
};

export const FieldCategory = ({
	children,
	categoryIndex,
	category,
	hasMarginTop = true,
	fieldsDocumentationUrl,
}: Props) => {
	const { formatMessage } = useIntl();
	const BeforeItems = category.configuration.renderBeforeItems;
	const id = `${category.configuration.name}-category-field-section-list`;
	return (
		<RoundedColumnNew
			aria-label={formatMessage(category.configuration.displayName)}
			tabIndex={0}
			id={id}
			removeSpaceAbove={!!category.configuration.dividerText}
		>
			{
				// If a divider is requested for the top bucket, it does not
				// make sense so the normal header is rendered instead
			}
			{category.configuration.dividerText && categoryIndex !== 0 ? (
				<Divider
					name={category.configuration.name}
					dividerText={category.configuration.dividerText}
				/>
			) : (
				<ColumnHeader hasMarginTop={hasMarginTop}>
					<Title>{formatMessage(category.configuration.displayName)}</Title>
					{renderWhatIsThis({
						category,
						fieldsDocumentationUrl,
						formatMessage,
						whatsThisLabelIdRef: id,
					})}
				</ColumnHeader>
			)}
			<Column>
				{BeforeItems ? <BeforeItems /> : null}
				{children}
			</Column>
		</RoundedColumnNew>
	);
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Props as FieldCategoryProps };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RoundedColumnNew = styled2.div<{
	children?: ReactNode;
	removeSpaceAbove?: boolean;
	enableFullSizeDropZone?: boolean;
	tabIndex?: number;
	id?: string;
}>({
	borderRadius: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: ({ removeSpaceAbove }) =>
		`${removeSpaceAbove ? token('space.negative.250') : token('space.0')} ${token('space.negative.100')} ${token('space.200')}`,
	paddingTop: token('space.0'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.100'),
	/**
	 * height: 100 - 16px is to leave a gap at the bottom to be consistent with the sides
	 * auto is the default value, i.e. if not props do default height in css
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.enableFullSizeDropZone ? `calc(100% - ${token('space.200')})` : 'auto'),
	/**
	 * content-box is the default value, i.e. if not props do default height in css
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxSizing: (props) => (props.enableFullSizeDropZone ? 'border-box' : 'content-box'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnHeader = styled2.div<{
	hasMarginTop: boolean;
	children?: ReactNode;
}>({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	justifyContent: 'flex-start',
	gap: token('space.100'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ hasMarginTop }) =>
		`${hasMarginTop ? token('space.150') : token('space.0')} ${token('space.0')} ${token('space.050')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RoundedColumn = styled<{
	children?: ReactNode;
	removeSpaceAbove?: boolean;
	enableFullSizeDropZone?: boolean;
}>(Column)(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		borderRadius: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: props.removeSpaceAbove ? token('space.negative.250') : token('space.0'),
		marginBottom: token('space.200'),
		/* According to the flexbox spec, flex items should not shrink below there min-content size. But Safari does not
        respect this behaviour when the item is display flex, hence flex-shrink: 0. */
		flexShrink: 0,
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ enableFullSizeDropZone }) =>
		enableFullSizeDropZone
			? /* height: 100 - 16px is to leave a gap at the bottom to be consistent with the sides */
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					height: `calc(100% - ${token('space.200')})`,
					boxSizing: 'border-box',
				})
			: '',
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleBase = styled.h2({
	/* Use a line height which matches the board's column titles. */
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.300'),
	/* Shrink when there is not enough space inline */
	minWidth: token('space.0'),
	flexShrink: 1,
	/* Hiding overflow to prevent scrollbars from showing up for Windows fonts (see MC-2533) */
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Title: ComponentType<{
	children?: ReactNode;
	// FIXME: type error on typography mixin, typing on `fontWeight` too strict
	// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(TitleBase)`
	font: ${token('font.heading.small')};
`;
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export {
	WhatIsThis,
	WhatIsThisWithInProductHelp,
	RoundedColumn as FieldCategoryRoundedColumn,
	ColumnHeader as FieldCategoryColumnHeader,
};
